<script lang="ts">
  interface HourData {
    start: string | null;
    end: string | null;
    is_closed: boolean;
  }

  interface ParsedHours {
    regularHours: Record<string, HourData & { label: string }>;
    specialDates: Record<string, HourData>;
  }

  let { hoursData }: { hoursData: any } = $props();

  // Parse the business hours data with error handling
  function parseBusinessHours(data: any): ParsedHours | null {
    try {
      let parsedData = data;

      // If data is a string, try to parse it as JSON
      if (typeof data === "string") {
        try {
          parsedData = JSON.parse(data);
        } catch (jsonError) {
          // Try to parse Python-style dictionary string
          try {
            const fixedJson = data
              // Replace single quotes with double quotes
              .replace(/'/g, '"')
              // Replace Python booleans/None with JSON equivalents
              .replace(/\bFalse\b/g, "false")
              .replace(/\bTrue\b/g, "true")
              .replace(/\bNone\b/g, "null");
            parsedData = JSON.parse(fixedJson);
          } catch (pythonError) {
            console.warn(
              "Failed to parse JSON/Python string:",
              jsonError,
              pythonError
            );
            return null;
          }
        }
      }

      if (!parsedData || typeof parsedData !== "object") {
        return null;
      }

      const parsed: ParsedHours = {
        regularHours: {},
        specialDates: {},
      };

      const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const dayLabels: Record<string, string> = {
        Mon: "Hétfő",
        Tue: "Kedd",
        Wed: "Szerda",
        Thu: "Csütörtök",
        Fri: "Péntek",
        Sat: "Szombat",
        Sun: "Vasárnap",
      };

      for (const [key, value] of Object.entries(parsedData)) {
        // Validate that value has expected structure
        if (!value || typeof value !== "object" || !("is_closed" in value)) {
          continue;
        }

        if (dayNames.includes(key)) {
          parsed.regularHours[key] = {
            ...(value as HourData),
            label: dayLabels[key],
          };
        } else {
          parsed.specialDates[key] = value as HourData;
        }
      }

      return parsed;
    } catch (error) {
      console.error("Error parsing business hours:", error);
      return null;
    }
  }

  // Format time display
  function formatTimeRange(
    start: string | null,
    end: string | null,
    isClosed: boolean
  ): string {
    if (isClosed || !start || !end) {
      return "Zárva";
    }
    return `${start} - ${end}`;
  }

  let parsedHours = $derived.by(() => parseBusinessHours(hoursData));
  let dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  let fallbackString = $derived(
    typeof hoursData === "string"
      ? hoursData
      : JSON.stringify(hoursData, null, 2)
  );
</script>

<!-- Rest of the template remains the same -->
{#if parsedHours}
  <h3 class="font-extrabold text-gray-800 mb-4">Nyitvatartás</h3>
  <div class="px-4">
    <!-- Regular Hours -->
    <div class="space-y-2">
      {#each dayOrder as day}
        {#if parsedHours.regularHours[day]}
          {@const hours = parsedHours.regularHours[day]}
          <div
            class="flex font-bold justify-between items-center py-3 border-b border-gray-100 last:border-b-0"
          >
            <div class="text-gray-700">{hours.label}</div>
            <div
              class="px-3 py-1 rounded-md text-sm font-medium {hours.is_closed
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700'}"
            >
              {formatTimeRange(hours.start, hours.end, hours.is_closed)}
            </div>
          </div>
        {/if}
      {/each}
    </div>

    <!-- Special Dates -->
    {#if Object.keys(parsedHours.specialDates).length > 0}
      <div class="mt-6 pt-4 border-t-2 border-gray-200">
        <h4 class="text-base font-extrabold text-gray-600 mb-3">
          Speciális nyitvatartás
        </h4>
        <div class="space-y-2">
          {#each Object.entries(parsedHours.specialDates) as [date, hours]}
            <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div
                class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2"
              >
                <div class="text-sm italic text-gray-600 flex-1">{date}</div>
                <div
                  class="px-3 py-1 rounded-md text-sm font-medium self-end {hours.is_closed
                    ? 'bg-red-100 text-red-700'
                    : 'bg-green-100 text-green-700'}"
                >
                  {formatTimeRange(hours.start, hours.end, hours.is_closed)}
                </div>
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </div>
{:else}
  <div class="max-w-md bg-gray-50 rounded-lg border border-gray-200 p-4">
    <h3 class="text-lg font-medium text-gray-800 mb-2">Nyitvatartás</h3>
    <pre
      class="text-sm text-gray-600 whitespace-pre-wrap break-words font-mono">{fallbackString}</pre>
  </div>
{/if}
